/**
 * lib/otdReportExport.js
 * Excel (.xlsx) and PDF exports for the OTD Command Board drill-down reports
 * (GET /api/otd/drilldown[/{key}]). A report is
 *   { key, title, count, sections: [{ title, columns: [{key,label,type}], rows }] }
 * and column `type` is text | number | date | money, so every tile's report
 * goes through the same code.
 */
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { fmtDay } from "@/lib/otdSummary";

const stamp = () => new Date().toISOString().slice(0, 10);
const safeName = (s) => String(s || "report").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");

export function fmtMoney(row, v) {
  const n = Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${row?.currency || "INR"} ${n}`;
}

/** Display text of one cell (drawer + PDF). */
export function cellText(col, row) {
  const v = row[col.key];
  if (v === null || v === undefined || v === "") return col.type === "number" || col.type === "money" ? (v === 0 ? "0" : "—") : "—";
  if (col.type === "date") return fmtDay(v);
  if (col.type === "money") return fmtMoney(row, v);
  if (col.type === "number") return Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 });
  return String(v);
}

// Excel keeps numbers numeric; money gets its own currency column when the
// section has a money column, so totals can be summed in the sheet.
function excelValue(col, row) {
  const v = row[col.key];
  if (v === null || v === undefined) return "";
  if (col.type === "date") return v ? fmtDay(v) : "";
  if (col.type === "number" || col.type === "money") return Number(v);
  return v;
}

function sectionAoa(section) {
  const hasMoney = section.columns.some((c) => c.type === "money");
  const header = section.columns.map((c) => c.label);
  if (hasMoney) header.push("Currency");
  const rows = section.rows.map((r) => {
    const out = section.columns.map((c) => excelValue(c, r));
    if (hasMoney) out.push(r.currency || "INR");
    return out;
  });
  return [header, ...rows];
}

/** One worksheet for a report — sections stacked, each under its title. */
export function reportSheet(report, generatedAt) {
  const aoa = [[report.title], [`Generated: ${generatedAt ? new Date(generatedAt).toLocaleString("en-IN") : new Date().toLocaleString("en-IN")}`]];
  let width = 1;
  report.sections.forEach((s) => {
    aoa.push([]);
    if (report.sections.length > 1 || s.title) aoa.push([`${s.title} (${s.rows.length})`]);
    const block = sectionAoa(s);
    width = Math.max(width, block[0].length);
    aoa.push(...block);
    if (!s.rows.length) aoa.push(["No records"]);
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = Array.from({ length: width }, (_, i) => ({ wch: i === 0 ? 18 : 20 }));
  return ws;
}

// Excel sheet names: ≤31 chars, no []:*?/\ and unique within the workbook.
function sheetName(title, used) {
  let base = String(title).replace(/[[\]:*?/\\]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31) || "Report";
  let name = base;
  let i = 2;
  while (used.has(name.toLowerCase())) {
    const suffix = ` (${i++})`;
    name = base.slice(0, 31 - suffix.length) + suffix;
  }
  used.add(name.toLowerCase());
  return name;
}

const SHEET_TITLE = {
  ppc: "PPC - Open TOs", purchase: "Purchase - Open POs", stores: "Stores - Pending GRNs",
  production: "Production - Jobs not at FG", quality: "Quality - Pending QC",
  sales_despatch: "Sales & Despatch", overdue: "Overdue Orders", red: "Red Orders",
  value_at_risk: "Value at Risk", on_track: "On Track", rag_red: "Red Pending Orders", rag_amber: "Amber Pending Orders",
};

export function exportReportExcel(report, generatedAt) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, reportSheet(report, generatedAt), sheetName(SHEET_TITLE[report.key] || report.title, new Set()));
  XLSX.writeFile(wb, `OTD_${safeName(SHEET_TITLE[report.key] || report.title)}_${stamp()}.xlsx`);
}

/** "Export All Reports" — one workbook, one sheet per report. */
export function exportAllReportsExcel(reports, generatedAt) {
  const wb = XLSX.utils.book_new();
  const used = new Set();
  reports.forEach((r) => XLSX.utils.book_append_sheet(wb, reportSheet(r, generatedAt), sheetName(SHEET_TITLE[r.key] || r.title, used)));
  XLSX.writeFile(wb, `OTD_All_Reports_${stamp()}.xlsx`);
}

// ---------------------------------------------------------------- PDF
// Built-in PDF fonts have no ₹ glyph, so money is printed with its ISO code.
export function exportReportPdf(report, generatedAt) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 32;
  const lineH = 11;
  let y = M;

  const footer = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`OTD Command Board · ${report.title}`, M, pageH - 16);
    doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - M, pageH - 16, { align: "right" });
    doc.setTextColor(0);
  };
  const newPage = () => { footer(); doc.addPage(); y = M; };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(report.title, M, y + 4);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Generated ${generatedAt ? new Date(generatedAt).toLocaleString("en-IN") : new Date().toLocaleString("en-IN")} · ${report.count} record(s)`, M, y);
  doc.setTextColor(0);
  y += 14;

  report.sections.forEach((s) => {
    const cols = s.columns;
    const avail = pageW - 2 * M;
    // Width by content: header and up to 200 rows sampled, then scaled to fit.
    const sample = s.rows.slice(0, 200);
    const raw = cols.map((c) => {
      const lens = sample.map((r) => cellText(c, r).length);
      return Math.min(40, Math.max(c.label.length, ...(lens.length ? lens : [4]))) + 2;
    });
    const sum = raw.reduce((a, b) => a + b, 0);
    const widths = raw.map((w) => (w / sum) * avail);

    if (y > pageH - 80) newPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`${s.title} (${s.rows.length})`, M, y + 8);
    y += 16;

    const drawHeader = () => {
      doc.setFillColor(241, 245, 249);
      doc.rect(M, y, avail, 16, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      let x = M;
      cols.forEach((c, i) => {
        const right = c.type === "number" || c.type === "money";
        doc.text(c.label, right ? x + widths[i] - 3 : x + 3, y + 11, right ? { align: "right" } : undefined);
        x += widths[i];
      });
      y += 18;
      doc.setFont("helvetica", "normal");
    };
    drawHeader();
    if (!s.rows.length) {
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text("No records", M + 3, y + 8);
      doc.setTextColor(0);
      y += 16;
    }
    s.rows.forEach((r, ri) => {
      doc.setFontSize(8);
      const cells = cols.map((c, i) => doc.splitTextToSize(cellText(c, r), widths[i] - 6));
      const h = Math.max(...cells.map((c) => c.length)) * lineH + 4;
      if (y + h > pageH - 32) { newPage(); drawHeader(); doc.setFontSize(8); }
      if (ri % 2 === 1) { doc.setFillColor(248, 250, 252); doc.rect(M, y - 2, avail, h, "F"); }
      let x = M;
      cols.forEach((c, i) => {
        const right = c.type === "number" || c.type === "money";
        cells[i].forEach((ln, li) => {
          const ty = y + 8 + li * lineH;
          if (right) doc.text(ln, x + widths[i] - 3, ty, { align: "right" });
          else doc.text(ln, x + 3, ty);
        });
        x += widths[i];
      });
      y += h;
    });
    y += 10;
  });
  footer();
  doc.save(`OTD_${safeName(SHEET_TITLE[report.key] || report.title)}_${stamp()}.pdf`);
}
